import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	Box,
	Checkbox,
	FormControlLabel,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useCustomTableColumns } from "../hooks/useCustomTableColumns";
import { useSearchEvents } from "../hooks/useSearchEvents";
import { ColumnDef, ProcessedRow } from "../models/utils";
import { billetStatus } from "../shared/billetStatus";
import { TextFieldSearchFilter } from "../shared/TextFieldSearchFilter";
import {
	calculateColumnWidths,
	calculateRowSpans,
	filterProcessedData,
	processEventsToRows,
} from "../utils/tableUtils";

interface CustomEventsTableProps {
	month: number;
	year: number;
	onError: (error: Error) => void;
}

export const CustomEventsTable = ({
	month,
	year,
	onError,
}: CustomEventsTableProps) => {
	const [filterValue, setFilterValue] = useState("");
	const [useColors, setUseColors] = useState(false);
	const [hideOtherEvents, setHideOtherEvents] = useState(true);
	const [hoveredBilletStatusCode, setHoveredBilletStatusCode] = useState<
		string | null
	>(null);

	const firstDayOfMonth = useMemo(
		() => new Date(year, month, 1, 0, 0, 0),
		[year, month],
	);
	const lastDayOfMonth = useMemo(
		() => new Date(year, month + 1, 0, 23, 59, 59),
		[year, month],
	);

	const {
		data,
		isLoading,
		error: e,
		isError,
	} = useSearchEvents({
		start: firstDayOfMonth,
		end: lastDayOfMonth,
	});

	const { visibleColumns, getRowBackgroundColor } = useCustomTableColumns({
		calendarMonth: month,
	});

	const processedData = useMemo(
		() => processEventsToRows(data, month, year),
		[data, month, year],
	);
	const filteredData = useMemo(() => {
		const base = hideOtherEvents
			? processedData.filter((row) => row.EventTypeCode !== "O")
			: processedData;
		return filterProcessedData(base, filterValue);
	}, [processedData, filterValue, hideOtherEvents]);
	const rowSpans = useMemo(
		() => calculateRowSpans(filteredData, visibleColumns),
		[filteredData, visibleColumns],
	);
	const columnWidths = useMemo(
		() => calculateColumnWidths(visibleColumns),
		[visibleColumns],
	);

	useEffect(() => {
		if (e && isError) {
			onError(e);
		}
	}, [e, isError]);

	const renderCellContent = (row: ProcessedRow, col: ColumnDef) => {
		if (col.render) {
			const value = col.getValue ? col.getValue(row) : (row as any)[col.field];
			return col.render(row, value);
		}

		const value = col.getValue ? col.getValue(row) : (row as any)[col.field];
		if (col.formatValue && value != null) {
			return col.formatValue(value);
		}

		return value ?? "";
	};

	return (
		<Box
			sx={{
				height: "100%",
				display: "grid",
				gridTemplateRows: "auto minmax(0, 1fr)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap",
					columnGap: 1.5,
					rowGap: 0.25,
				}}
			>
				<Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
					<TextFieldSearchFilter
						value={filterValue}
						onChange={(val) => setFilterValue(val)}
					/>
				</Box>
				<FormControlLabel
					sx={{ whiteSpace: "nowrap", mr: 0 }}
					control={
						<Checkbox
							checked={useColors}
							onChange={(event) => setUseColors(event.target.checked)}
							color="primary"
							size="small"
						/>
					}
					label="Difference sections with colors"
					slotProps={{ typography: { variant: "body2" } }}
				/>
				<FormControlLabel
					sx={{ whiteSpace: "nowrap", mr: 0 }}
					control={
						<Checkbox
							checked={hideOtherEvents}
							onChange={(event) => setHideOtherEvents(event.target.checked)}
							color="primary"
							size="small"
						/>
					}
					label='Hide "Other" events'
					slotProps={{ typography: { variant: "body2" } }}
				/>
			</Box>

			<Box
				sx={{
					width: "100%",
					marginTop: 1,
					minHeight: 0,
					display: "grid",
					gridTemplateRows: "minmax(0, 1fr) auto",
				}}
			>
				<TableContainer
					component={Paper}
					elevation={0}
					sx={{
						minHeight: 0,
						overflow: "auto",
						border: "1px solid #ccc",
						borderRadius: "4px",
					}}
				>
					<Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
						<colgroup>
							{visibleColumns.map((col, idx) => (
								<col
									key={col.field}
									style={{
										width: columnWidths[idx].width,
										minWidth: col.minWidth,
									}}
								/>
							))}
						</colgroup>
						<TableHead>
							<TableRow>
								{visibleColumns.map((col) => (
									<TableCell
										key={col.field}
										align={col.align || "left"}
										sx={{
											backgroundColor: "#fff",
											fontWeight: 600,
											fontSize: 11,
											lineHeight: 2,
											py: 0,
											px: 0.25,
											boxSizing: "border-box",
											verticalAlign: "middle",
										}}
									>
										{col.headerTooltip ? (
											<Tooltip title={col.headerTooltip} arrow placement="top">
												<span>{col.headerName}</span>
											</Tooltip>
										) : (
											col.headerName
										)}
										{col.headerSub && (
											<Box sx={{ lineHeight: 1.15, pb: 0.25 }}>
												<Box
													sx={{
														fontSize: 9,
														fontWeight: 400,
														color: "#666",
													}}
												>
													{col.headerSub.top}
												</Box>
												<Box sx={{ fontSize: 9, fontWeight: 700 }}>
													{col.headerSub.bottom}
												</Box>
											</Box>
										)}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={visibleColumns.length} align="center">
										<CenteredLazyLoading />
									</TableCell>
								</TableRow>
							) : filteredData.length === 0 ? (
								<TableRow>
									<TableCell colSpan={visibleColumns.length} align="center">
										No events found
									</TableCell>
								</TableRow>
							) : (
								filteredData.map((row, rowIndex) => {
									const rowKey = `${row.EventId}-${row.RowIndex ?? rowIndex}`;

									return (
										<TableRow
											key={rowKey}
											sx={{
												backgroundColor: getRowBackgroundColor({
													row,
													hoveredBilletStatusCode,
													useColors,
												}),
												borderBottom: "1px solid rgba(224, 224, 224, 1)",
												position: "relative",
												"& td": {
													fontWeight:
														row.StatusCode !== "P" && row.EventId > 0
															? "bold"
															: "normal",
												},
											}}
										>
											{visibleColumns.map((col, colIndex) => {
												const cellKey = `${rowIndex}-${col.field}`;
												if (rowSpans?.skipRows.has(cellKey)) {
													return null; // Skip this cell, it's merged
												}
												const span = rowSpans?.spans.get(cellKey) || 1;

												return (
													<TableCell
														key={col.field}
														rowSpan={
															col.spanRows || col.spanRowsByDay ? span : 1
														}
														align={col.align || "left"}
														sx={{
															width: columnWidths[colIndex].width,
															minWidth: col.minWidth,
															fontSize: 10.5,
															lineHeight: 1.2,
															py: 0.25,
															px: 0.25,
															boxSizing: "border-box",
															...col.cellStyle?.(row),
														}}
													>
														{renderCellContent(row, col)}
													</TableCell>
												);
											})}
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				</TableContainer>

				{/* Legend: billet colors 2x2 on the left (~2/3), stacked
				    Scheduled/Actual reference on the right (~1/3). */}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "2fr 1fr",
						alignItems: "center",
						gap: 1,
						mt: 1,
					}}
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
							gap: 0.25,
						}}
					>
						{billetStatus.map((type) => (
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									width: "fit-content",
									cursor: "pointer",
									px: 1,
									py: 0.5,
									borderRadius: 1,
									backgroundColor:
										hoveredBilletStatusCode === type.code
											? "#e0e0e0"
											: "#fcfcfcff",
									transition: "background-color 0.2s",
								}}
								key={type.code}
								onMouseEnter={() => setHoveredBilletStatusCode(type.code)}
								onMouseLeave={() => setHoveredBilletStatusCode(null)}
							>
								<Box
									sx={{
										width: 15,
										height: 15,
										borderRadius: 4,
										backgroundColor: type.backgroundColor,
									}}
								/>
								<Typography variant="body2" color="textSecondary">
									Billets {type.name}
								</Typography>
							</Box>
						))}
					</Box>
					{/* Mirrors the stacked cells: gray top line, bold bottom line. */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							lineHeight: 1.35,
							fontSize: 12,
							textAlign: "right",
						}}
					>
						<Box>
							<Box sx={{ color: "#666" }}>Scheduled</Box>
							<Box sx={{ fontWeight: "bold" }}>Actual</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
